import { PivotItem } from '@fluentui/react';
import { useBoolean } from '@uifabric/react-hooks';
import { HidableWidgetType } from 'features/Rooms/PersonalDashboard/widgetVisibility';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { idpService } from 'services/api/identity-provider.service';
import { useHiddenDashboardWidgets } from 'shared-state/directory/hooks';
import { useIdentityClaims } from 'shared-state/identity/hooks';

export interface IIdentityPanelAction {
    title: string;
    action?: (e: any) => void;
    key: string;
    disabled?: boolean;
    symbol: string;
}

export interface IIdentityPanelState {
    onPanelDismiss: Function;
    isPanelOpen: boolean;
    children?: React.ReactNode;
    visitorCheck: { isVisitor: boolean; visitorId: string };
    selectedKey: string;
    handleLinkClick: (item?: PivotItem) => void;
    draftHiddenWidgets: string[];
    isDirty: boolean;
    isSaving: boolean;
    onToggleWidget: (type: HidableWidgetType, hidden: boolean) => void;
    onSaveWidgets: () => Promise<void>;
    hideErrorDialog: boolean;
    toggleHideErrorDialog: () => void;
}

export const useIdentityPanelState: () => IIdentityPanelState = () => {
    const location = useLocation();
    const isPanelOpen = ['#status', '#edit-profile', '#preferences'].includes(location.hash);
    const [identity] = useIdentityClaims();
    const visitorCheck =
        identity?.roles.includes('visitor') || identity?.roles.includes('auto-confirmed-visitor')
            ? { isVisitor: true, visitorId: identity?.uid }
            : { isVisitor: false, visitorId: '' };

    const onPanelDismiss = () => {
        window.location.href = '#';
    };

    // Tab selection is plain component state — deliberately not hash-routed, since the panel's
    // own open/closed state already owns the hash.
    const [selectedKey, setSelectedKey] = useState<string>('profile');
    const handleLinkClick = (item?: PivotItem) => {
        if (item) setSelectedKey(item.props.itemKey || '');
    };

    const [savedHiddenWidgets, setSavedHiddenWidgets] = useHiddenDashboardWidgets();
    const [draftHiddenWidgets, setDraftHiddenWidgets] = useState<string[]>(savedHiddenWidgets);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [hideErrorDialog, { toggle: toggleHideErrorDialog }] = useBoolean(true);

    // Read through a ref so the re-seed effect below can depend on `isPanelOpen` alone without
    // the saved value itself being a dependency.
    const savedHiddenWidgetsRef = useRef<string[]>(savedHiddenWidgets);
    savedHiddenWidgetsRef.current = savedHiddenWidgets;

    // True while the draft holds local toggles the user hasn't saved or cancelled yet. Guards the
    // sync effect below from overwriting an edit in progress.
    const hasUnsavedEditsRef = useRef<boolean>(false);

    // Re-seed the draft only on panel open/close transitions — never on every render — so an
    // in-progress edit isn't clobbered while the panel stays open (including while switching
    // between the Profile and Widgets tabs), but closing (or reopening later) silently discards
    // whatever was mid-edit, with no confirm dialog.
    useEffect(() => {
        hasUnsavedEditsRef.current = false;
        setDraftHiddenWidgets(savedHiddenWidgetsRef.current ?? []);
    }, [isPanelOpen]);

    // Adopt a saved value that lands while the panel is already open — the profile fetch resolving
    // after a reload on #preferences, or the canonical array coming back from a successful save.
    // Skipped whenever the user has unsaved toggles, so this can never clobber an edit in progress.
    useEffect(() => {
        if (!hasUnsavedEditsRef.current) setDraftHiddenWidgets(savedHiddenWidgets ?? []);
    }, [savedHiddenWidgets]);

    const isDirty = useMemo(
        () => JSON.stringify([...draftHiddenWidgets].sort()) !== JSON.stringify([...(savedHiddenWidgets ?? [])].sort()),
        [draftHiddenWidgets, savedHiddenWidgets]
    );

    const onToggleWidget = (type: HidableWidgetType, hidden: boolean) => {
        hasUnsavedEditsRef.current = true;
        setDraftHiddenWidgets((previouslyHidden) =>
            hidden ? Array.from(new Set([...previouslyHidden, type])) : previouslyHidden.filter((hiddenType) => hiddenType !== type)
        );
    };

    const onSaveWidgets = async () => {
        setIsSaving(true);
        try {
            const updatedUser = await idpService.updateHiddenDashboardWidgets(draftHiddenWidgets);
            hasUnsavedEditsRef.current = false;
            setSavedHiddenWidgets(updatedUser.hidden_dashboard_widgets ?? []);
        } catch (error) {
            // Keep the draft as-is so the user can retry; surface the same error dialog
            // PersonalPreferences shows when its PATCH fails.
            console.error(error);
            toggleHideErrorDialog();
        } finally {
            setIsSaving(false);
        }
    };

    return {
        isPanelOpen,
        onPanelDismiss,
        visitorCheck,
        selectedKey,
        handleLinkClick,
        draftHiddenWidgets,
        isDirty,
        isSaving,
        onToggleWidget,
        onSaveWidgets,
        hideErrorDialog,
        toggleHideErrorDialog
    };
};
